/**
 * The in-place explanation layer, shared by admin and the management plane.
 *
 * The rule this encodes is progressive disclosure, not simplification. A
 * console screen leads with the precise word an engineer would use - `p95`,
 * `5xx`, `dead-lettered` - because a power user scans for exactly that string,
 * and the plain-language sentence rides along in a tooltip that is reachable by
 * hover, by focus and by the accessible name. Nothing is renamed to something
 * friendlier, nothing is hidden behind a click, and there is no glossary page
 * to go and read first.
 *
 * `CONSOLE_GLOSSARY` is deliberately conservative. A term only appears here if
 * its meaning is certain; a confident wrong definition in front of an operator
 * is worse than an unexplained word, so anything ambiguous is left plain.
 */

import type { ReactNode } from 'react';
import { Tooltip, TooltipTrigger } from '../components/base/tooltip/tooltip';
import { cx } from '../utils/cx';

/**
 * Plain-language definitions for the technical vocabulary these consoles show.
 * Keys are lowercase so a caller can look a term up without worrying about how
 * it is capitalised on screen.
 */
export const CONSOLE_GLOSSARY: Record<string, string> = {
  /* --- latency and HTTP --- */
  p50: 'The middle of the range: half of all calls finished faster than this, half slower.',
  p95: '95% of calls finished faster than this. The slow tail, not the typical case.',
  p99: '99% of calls finished faster than this. The worst 1%, where timeouts show up.',
  percentile:
    'A ranking rather than an average: the p95 is the value 95% of measurements came in under.',
  latency: 'How long a call took from the moment it arrived to the moment we answered it.',
  '2xx': 'Successful responses - we handled the request and answered normally.',
  '4xx': 'Client errors - the request was malformed, unauthorised, or asked for something absent.',
  '5xx': 'Server errors - the request reached us and we failed to handle it.',
  'status code': 'The three-digit result of an HTTP call: 2xx succeeded, 4xx and 5xx did not.',

  /* --- queues, jobs and retries --- */
  queued: 'Accepted and waiting for a worker to pick it up. Nothing has run yet.',
  running: 'A worker has picked this up and is working on it now.',
  retry_wait: 'It failed, and it is waiting out a back-off delay before the next attempt.',
  dead_lettered:
    'It used up every attempt and was set aside instead of retried again. It will not run again unless an operator redrives it.',
  'dead letter':
    'Where work goes after its final attempt fails: set aside for a human rather than retried forever.',
  'durable job':
    'A job written to the database before it runs, so it survives a restart and can be inspected afterwards.',
  checkpoint:
    'A record a job writes when it finishes a stage, so a later attempt can see how far the run got.',
  idempotency:
    'A key sent with a write so that repeating the same request applies the change once rather than twice.',
  'idempotency key':
    'A key sent with a write so that repeating the same request applies the change once rather than twice.',
  redrive: 'Putting a job that had stopped back into the queue so it runs again for real.',
  backlog: 'Work that has been accepted but not yet run.',

  /* --- pagination and identifiers --- */
  cursor:
    'A marker for where the previous page ended. The next page starts from it, so rows are never skipped or repeated while the data changes underneath.',
  'keyset pagination':
    'Paging by "everything after this row" rather than by a page number. It stays correct on a table being written to, but it cannot report a total.',
  offset: 'How many rows were skipped before this page starts.',
  uuid: 'A 36-character identifier generated to be unique without a central registry.',
  'correlation id':
    'One identifier shared by every event from the same request or flow, so they can be tied back together afterwards.',
  'sha-256': 'A fingerprint of some content. Identical content always produces the same digest.',
  digest: 'A fingerprint of some content. Identical content always produces the same digest.',

  /* --- storage, delivery and mobile --- */
  'signed url':
    'A temporary link that carries its own permission and stops working once it expires. It cannot be shared usefully after that.',
  ttl: 'Time to live: how long this stays valid before it expires.',
  ota: 'Over the air: a JavaScript-only app update delivered without going through the app stores.',
  'update channel':
    'Which stream of over-the-air updates a device is on. `production` is members; `production-internal` is our own builds.',
  'keyed hash':
    'The value scrambled with a secret key. The same input always produces the same output, so records can be matched, but the original cannot be recovered from it.',
  suppressed:
    'The mail provider will not deliver to this address any more, usually after it bounced or the recipient complained.',

  /* --- lifecycle and safety --- */
  'soft delete':
    'The record is marked deleted and hidden from the product, but the row is still there and can be brought back.',
  'hard delete': 'The data is actually removed. Nothing can bring it back afterwards.',
  'grace period': 'The window after a deletion request during which it can still be reversed.',
  'expected version':
    'The version you were last shown. If someone else changed the record in the meantime the write is refused rather than silently overwriting them.',
  conflict:
    'The record changed after you read it, so the write was refused rather than overwriting someone else.',
  audited: 'The change and your reason for it are written to a log that cannot be edited.',
  'feature flag':
    'A switch that turns a capability on or off for some share of users without a new release.',
  'rollout percentage': 'The share of users the flag is turned on for.',
  sla: 'Service level agreement: the deadline this work is expected to be finished by.',
};

export function definitionFor(term: string): string | undefined {
  return CONSOLE_GLOSSARY[term.trim().toLowerCase()];
}

/**
 * The precise term, rendered verbatim, with its plain-language meaning attached
 * in place.
 *
 * The trigger is a real button so it is reachable by Tab as well as by hover,
 * and its accessible name carries the whole definition so a screen reader and a
 * test both get the sentence without opening anything. The visible text stays
 * exactly the term, and stays selectable, so searching the page for `5xx` still
 * finds `5xx`.
 */
export function Term({
  term,
  definition,
  children,
  className,
}: {
  /** The technical word itself, shown verbatim unless `children` overrides it. */
  term: string;
  /** Overrides the glossary. Pass this when the term is not a shared one. */
  definition?: string;
  children?: ReactNode;
  className?: string;
}) {
  const meaning = definition ?? definitionFor(term);
  if (!meaning) {
    // Never invent an explanation: an undefined term renders as itself.
    return <span className={className}>{children ?? term}</span>;
  }
  return (
    <Tooltip title={term} description={meaning}>
      <TooltipTrigger
        aria-label={`${term}: ${meaning}`}
        data-term={term}
        className={cx(
          'inline cursor-help select-text underline decoration-dotted decoration-from-font underline-offset-[3px]',
          className,
        )}
      >
        {children ?? term}
      </TooltipTrigger>
    </Tooltip>
  );
}

/**
 * A label that is mostly plain words with one technical term inside it, for a
 * field label or a table heading: `Cache (Valkey)`, `Server errors (5xx)`.
 * The human words lead, the precise identifier stays on screen beside them.
 */
export function LabelWithTerm({
  label,
  term,
  definition,
}: {
  label: string;
  term: string;
  definition?: string;
}) {
  return (
    <>
      {label} (<Term term={term} {...(definition ? { definition } : {})} />)
    </>
  );
}
